import { RoleStepKey } from '@internal/components/app/roles';
import {
  CALL_STEP_DESCRIPTION,
  TEST_STEP_DESCRIPTION,
} from '@internal/components/roles-hub/build-role-steps';
import {
  RoleStep,
  RoleStepStatus,
} from '@internal/components/roles-hub/roles-hub.types';

import {
  PAUSED_ROLE_TEXT,
  ROLE_CHECKS_TEXT,
  ROLE_NOT_VERIFIED_TEXT,
  VERIFIED_TEXT,
  WAITLIST_CAPACITY_TEXT,
} from './application-copy';
import {
  ApplicationAction,
  ApplicationLinkAction,
  ApplicationTarget,
  BuildInput,
  RoleDetailsModel,
  RoleStageModel,
  RoleStageStatus,
  StepModel,
  StepStatus,
  StepStatusTone,
} from './application-model.types';

const NOTICE_STATUSES: ReadonlySet<RoleStepStatus> = new Set([
  RoleStepStatus.Pending,
  RoleStepStatus.AwaitingReview,
  RoleStepStatus.Scheduled,
  RoleStepStatus.Error,
]);

const SETTLED_STATUSES: ReadonlySet<RoleStepStatus> = new Set([
  RoleStepStatus.Done,
  RoleStepStatus.Skipped,
]);

const DETAIL_STATUSES: Partial<Record<RoleStepStatus, StepStatus>> = {
  [RoleStepStatus.Scheduled]: {
    label: 'Scheduled',
    tone: StepStatusTone.Accent,
  },
  [RoleStepStatus.Error]: { label: 'Interrupted', tone: StepStatusTone.Error },
  [RoleStepStatus.Locked]: { label: 'Locked', tone: StepStatusTone.Muted },
};

const STAGE_GROUPS: { label: string; keys: RoleStepKey[] }[] = [
  { label: 'Skill test', keys: [RoleStepKey.Test] },
  { label: 'Call with Mark', keys: [RoleStepKey.Call] },
  { label: 'Review', keys: [RoleStepKey.Review] },
  {
    label: 'Interview',
    keys: [
      RoleStepKey.Recruiter,
      RoleStepKey.Technical,
      RoleStepKey.Verification,
    ],
  },
];

const APPLICATION_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
  [
    TEST_STEP_DESCRIPTION,
    'Start with a 15-minute quiz: mostly multiple choice, only about your stack. No algorithm puzzles.',
  ],
  [
    CALL_STEP_DESCRIPTION,
    'A 20-minute AI call about your experience before an interview with a team.',
  ],
]);

const joinText = (parts: (string | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

const describeStep = (step: RoleStep): string => {
  const text = step.description
    ? APPLICATION_DESCRIPTIONS.get(step.description) ?? step.description
    : undefined;
  return joinText([text, step.statusHint]);
};

const noticeText = (step: RoleStep): string => {
  const body = describeStep(step) || step.statusLabel;
  return body ? `${step.title}: ${body}` : step.title;
};

const scheduledNotices = (steps: RoleStep[]): string[] =>
  steps
    .filter(step => step.status === RoleStepStatus.Scheduled)
    .map(noticeText);

const isNotice = (step: RoleStep): boolean =>
  !SETTLED_STATUSES.has(step.status) &&
  step.status !== RoleStepStatus.Locked &&
  Boolean(
    step.statusLabel || step.statusHint || NOTICE_STATUSES.has(step.status)
  );

const stageStatus = (
  steps: RoleStep[],
  keys: RoleStepKey[],
  currentKey: RoleStepKey
): RoleStageStatus => {
  if (keys.includes(currentKey)) return RoleStageStatus.Current;
  const members = steps.filter(step => keys.includes(step.key));
  const settled =
    members.length > 0 &&
    members.every(step => SETTLED_STATUSES.has(step.status));
  return settled ? RoleStageStatus.Completed : RoleStageStatus.Upcoming;
};

const buildStages = (
  steps: RoleStep[],
  currentKey: RoleStepKey
): RoleStageModel[] =>
  STAGE_GROUPS.map(({ label, keys }) => ({
    label,
    status: stageStatus(steps, keys, currentKey),
  }));

const findCurrentStep = (steps: RoleStep[]): RoleStep | undefined =>
  steps.find(step => step.action) ??
  steps.find(step => NOTICE_STATUSES.has(step.status));

interface RoleStepContext {
  input: BuildInput;
  terminal: boolean;
  base: Pick<StepModel, 'key' | 'title' | 'done' | 'active'>;
  showActions: boolean;
  rolesTarget: ApplicationTarget;
  viewRole: ApplicationLinkAction;
}

const buildStoppedRoleStep = ({
  input: { roleProgress: rp },
  terminal,
  base,
  showActions,
  viewRole,
}: RoleStepContext): StepModel => {
  let action: ApplicationLinkAction | undefined;
  if (showActions && terminal) {
    action = viewRole;
  } else if (showActions) {
    action = {
      label: rp.outcomeLabel ?? 'Explore other roles',
      target: ApplicationTarget.RolesExplore,
    };
  }
  return {
    ...base,
    text: rp.capacityWaitlisted
      ? WAITLIST_CAPACITY_TEXT
      : ROLE_NOT_VERIFIED_TEXT,
    status: { label: 'Not verified', tone: StepStatusTone.Error },
    action,
  };
};

const buildVerifiedRoleStep = ({
  input: { roleSteps },
  base,
  showActions,
  viewRole,
}: RoleStepContext): StepModel => {
  const verification = roleSteps.find(
    step => step.key === RoleStepKey.Verification
  );
  return {
    ...base,
    text: joinText([
      verification?.description ?? VERIFIED_TEXT,
      ...scheduledNotices(roleSteps),
    ]),
    action: showActions ? viewRole : undefined,
  };
};

const buildPausedRoleStep = ({
  input: { roleSteps },
  base,
}: RoleStepContext): StepModel => ({
  ...base,
  text: joinText([PAUSED_ROLE_TEXT, ...scheduledNotices(roleSteps)]),
});

const buildIdleRoleStep = ({
  input: { hideRoleActions, roleProgress: rp },
  terminal,
  base,
  rolesTarget,
}: RoleStepContext): StepModel => ({
  ...base,
  text: ROLE_CHECKS_TEXT,
  active: !terminal && !hideRoleActions,
  action: hideRoleActions
    ? undefined
    : {
        label: terminal || rp.started ? 'View role progress' : 'Start',
        target: rolesTarget,
      },
});

const currentStepAction = (
  current: RoleStep,
  { input: { hideRoleActions }, terminal, viewRole }: RoleStepContext
): ApplicationAction | undefined => {
  if (hideRoleActions) return undefined;
  if (terminal) return viewRole;
  return current.action ? { ...current.action, primary: true } : undefined;
};

const buildCurrentRoleStep = (
  current: RoleStep,
  context: RoleStepContext
): StepModel => {
  const {
    input: { roleSteps },
    base,
  } = context;
  const notices = roleSteps
    .filter(step => step.key !== current.key && isNotice(step))
    .map(noticeText);
  const action = currentStepAction(current, context);

  const details: RoleDetailsModel = {
    stages: buildStages(roleSteps, current.key),
    title: current.title,
    text: joinText([describeStep(current), ...notices]),
    status: DETAIL_STATUSES[current.status],
    action,
  };

  return {
    ...base,
    text: '',
    active: Boolean(action?.primary),
    details,
  };
};

export const buildRoleStep = (
  input: BuildInput,
  terminal: boolean
): StepModel => {
  const { paused, hideRoleActions, roleProgress: rp, roleSteps } = input;
  const rolesTarget = rp.started
    ? ApplicationTarget.Roles
    : ApplicationTarget.RolesExplore;
  const context: RoleStepContext = {
    input,
    terminal,
    base: {
      key: 'role',
      title: `Verify ${rp.label || 'your'} role`,
      done: rp.verified,
      active: false,
    },
    showActions: !paused && !hideRoleActions,
    rolesTarget,
    viewRole: { label: 'View role', target: rolesTarget },
  };

  if (rp.stopped) return buildStoppedRoleStep(context);
  if (rp.verified) return buildVerifiedRoleStep(context);
  if (paused) return buildPausedRoleStep(context);

  const current = findCurrentStep(roleSteps);
  return current
    ? buildCurrentRoleStep(current, context)
    : buildIdleRoleStep(context);
};
